import i18n from "i18next";
/**
 * The pane surface switch — quiet chrome.
 *
 * Visual language (2026-07-18 founder UAT):
 * square corners, inset 1px border, no solid primary fill.
 * Active = subtle wash + weight; hover = background only.
 * Content stays the focus; chrome must not shout.
 *
 * Both panes carry the same strip and the same four surfaces. It used to be
 * two different strips with two different option lists, which said the panes
 * were different kinds of thing; they are not.
 */
import { IconBook2, IconEye, IconFileText, IconPencil, IconSparkles } from "@tabler/icons-react";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";
import type { PaneSurface } from "@/lib/workspacePanes";
import { useCanEdit } from "@/lib/documentAccess";

const ICONS = {
  flow: IconPencil,
  read: IconBook2,
  pdf: IconFileText,
  // Eye = output preview (not code) — distinct from per-block Source </>
  source: IconEye,
  ask: IconSparkles,
} as const;

export type PaneTabIcon = keyof typeof ICONS;

export interface PaneTabOption {
  id: string;
  label: string;
  title: string;
  icon: PaneTabIcon;
  testId?: string;
}

/**
 * The four surfaces, offered identically on both sides.
 *
 * Both panes get the same strip because both panes can hold anything: the
 * conversation, the editor, the compiled PDF, the source. Two strips that
 * differ would imply the panes differ, and they no longer do.
 */
export const PANE_TABS: PaneTabOption[] = [
  {
    id: "ask",
    label: i18n.t("editor:paneTabs.askLilia"),
    title: i18n.t("editor:paneTabs.theConversation"),
    icon: "ask",
    testId: testIds.chatFirst.paneTabAsk,
  },
  {
    id: "edit",
    label: i18n.t("editor:paneTabs.edit"),
    title: i18n.t("editor:paneTabs.writeTheDocument"),
    icon: "flow",
    testId: testIds.chatFirst.paneTabEdit,
  },
  {
    id: "pdf",
    label: i18n.t("editor:paneTabs.pdf"),
    title: i18n.t("editor:paneTabs.theCompiledPdf"),
    icon: "pdf",
    testId: testIds.chatFirst.paneTabPdf,
  },
  {
    id: "source",
    label: i18n.t("editor:paneTabs.source"),
    title: i18n.t("editor:paneTabs.theDocumentSourceLatexAnd"),
    icon: "source",
    testId: testIds.chatFirst.paneTabSource,
  },
];

/**
 * The tabs as this reader sees them: someone who can't edit reads, so "Edit"
 * is "Read" for them (Olivia, 10 Oct; the surface is the same document).
 */
export function useReaderTabs(tabs: PaneTabOption[]): PaneTabOption[] {
  const mayEdit = useCanEdit();
  return mayEdit ? tabs : tabs.map((t) => (t.id === "edit" ? { ...t, label: "Read", title: "Read the document", icon: "read" } : t));
}

/**
 * The segmented strip itself, with no opinion about what it switches.
 *
 * Two of these are on screen at once now — Edit · PDF · Source on the right,
 * Ask Lilia · Edit on the left — so they have to be the same control, or they
 * read as two competing ones.
 */
export function PaneTabs({
  options,
  value,
  onChange,
  label,
  testId,
}: {
  options: PaneTabOption[];
  value: string;
  onChange: (next: string) => void;
  label: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      // A true segmented control: one outline around the group, 30px like
      // every other control, 8px radius. It was 26px and rounded-none with an
      // inset shadow, which is why it read as loose text rather than one
      // control with four positions.
      className="inline-flex h-[var(--lilia-control-h,30px)] shrink-0 items-center gap-[var(--lilia-gap-seg,1px)] rounded-[var(--lilia-radius-control,8px)] border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface)] p-[2px]"
      role="tablist"
      aria-label={label}
    >
      {options.map((opt, i) => {
        const Icon = ICONS[opt.icon];
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            data-testid={opt.testId}
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            title={opt.title}
            className={cn(
              "inline-flex h-[24px] items-center gap-[5px] px-2.5 text-[length:var(--lilia-ui-label,12.5px)] font-medium transition-colors rounded-[var(--lilia-radius-nested,6px)]",
              i > 0 && "shadow-[inset_1px_0_0_0_var(--lilia-border,#e4e9f1)]",
              active
                ? "bg-[var(--lilia-primary)] text-[color:var(--lilia-on-primary)]"
                : "bg-transparent text-[color:var(--lilia-text-secondary)] hover:bg-[var(--lilia-hover)] hover:text-[color:var(--lilia-text)]",
            )}
          >
            <Icon size={12} stroke={1.75} />
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * One pane's strip.
 *
 * `value` is what this pane is showing; picking something the other pane holds
 * swaps them, which is the caller's job — see lib/workspacePanes.
 */
export function PaneSurfaceTabs({
  side,
  value,
  onChange,
}: {
  side: "left" | "right";
  value: PaneSurface;
  onChange: (next: PaneSurface) => void;
}) {
  return (
    <PaneTabs
      testId={side === "right" ? testIds.chatFirst.modeToggle : testIds.chatFirst.leftPaneToggle}
      label={side === "right" ? "Right pane" : "Left pane"}
      options={useReaderTabs(PANE_TABS)}
      value={value}
      onChange={(id) => onChange(id as PaneSurface)}
    />
  );
}

/** Shared 48px chrome height — both pane headers, flush with each other. */
export const CHAT_FIRST_HEADER_H = 48;

/**
 * The document's own surfaces: Edit · PDF · Source.
 *
 * The same strip as the panes carry, minus Ask Lilia. In the Side arrangement
 * Ask Lilia is already docked at the right edge and is not one of the things
 * the document column can become — offering it there would be a tab that moves
 * a panel that is already on screen.
 */
export const DOCUMENT_TABS: PaneTabOption[] = PANE_TABS.filter((t) => t.id !== "ask");

export type DocumentSurface = "edit" | "pdf" | "source";

export function DocumentSurfaceTabs({
  value,
  onChange,
}: {
  value: DocumentSurface;
  onChange: (next: DocumentSurface) => void;
}) {
  return (
    <PaneTabs
      testId={testIds.chatFirst.documentSurfaceTabs}
      label={i18n.t("editor:paneTabs.documentView")}
      options={useReaderTabs(DOCUMENT_TABS)}
      value={value}
      onChange={(id) => onChange(id as DocumentSurface)}
    />
  );
}
